import { arch }                   from '../../sass/button/arch/button.arch.class.scss'
import { ORIENTATIONS }           from '../../constants/orientation/orientations.constant'
import { UI_AUTOSPIN_DOM_CONFIG } from '../../constants/ui/ui-autospin-dom-config-constant'
import { UI_BET_DOM_CONFIG }      from '../../constants/ui/ui-bet-dom-config.constant'
import { UI_DOM_CONFIG }          from '../../constants/ui/ui-dom-config.constant'
import { UI_SIDES }               from '../../constants/ui/ui-sides.constant'

/**
 * @file Defines the {@link UIManager} class
 * @author Joe Thompson <joe.thompson@1x2network.com>
 */

 /** Class for handling UI styling */
 export class UIManager {

    /**
     * Reference to a DOM config
     * @memberof UIManager
     * @protected
     * @instance
     * @type {ui-dom-config[]}
     */
    _config

    /**
     * Array keeping reference of parent nodes when rendering DOM
     * @memberof UIManager
     * @protected
     * @instance
     * @type {HTMLElement[]}
     */
    _parents

    /** Render nodes needed for new UI */
    constructor(){

        //If mobile
        if (framework.isTouch()) {

            this._config  = UI_DOM_CONFIG //Store reference to UI DOM config
            this._parents = []            //Initialize parents array

            //Loop through DOM config
            for (let i = 0; i < this._config.length; i++) {
                
                let elem = this._config[i] //Store current element
                this.render(elem)         //Render element

            }

            this.hideOldUI(true) //Hide old mobile UI

            this.config = UI_BET_DOM_CONFIG      //Render UI BET DOM config
            this.config = UI_AUTOSPIN_DOM_CONFIG //Render UI AUTOSPIN DOM config

        }

        else {
            this.hideOldUI(false) //Hide old desktop UI
        }

    }

    /** Set config */
    set config(v_) {

        this._config = v_ //Update current DOM config

        //Loop through bet DOM config
        for (let i = 0; i < this._config.length; i++) {

            let elem = this._config[i] //Store current element
            this.render(elem)          //Render element

        }

        this._parents.shift() //Remove old parent

    }

    /**
     * Function to render part of the DOM based on config
     * @param {HTMLElement} n_ - HTML node
     */
    render(n_) {

        //If node has not been already rendered from existing iron dog framework
        if (n_.id !== 'gameFramework') {

            let elem = document.createElement(n_.element) //Create node

            //If element inherits classes
            if (n_.classList.length > 0) {
                this.classes(elem, n_) //Add classes
            }

            elem.id = n_.id                                        //Set class id
            let p   = document.getElementById(this._parents[0].id) //Store parent node
            p.appendChild(elem)                                    //Append node to parent

            this._parents.unshift(n_) //Store current parent node in parents array

            //If node has sibling nodes
            if (n_.siblings) {
                this.siblings(n_.siblings) //Render siblings
            }

        }

        //Add root iron dog node to parents array
        else {
            this._parents.unshift(n_)
        }

    }

    /**
     * Renders sibling nodes for part of the DOM based on config
     * @param {object} s_ - Siblings to render
     */
    siblings(s_) {

        //Loop through siblings
        for (let i = 0; i < s_.length; i++) {

            let s     = s_[i]                             //Store current sibling
            let sElem = document.createElement(s.element) //Create sibling node

            //If sibling inherits classes
            if (s.classList.length > 0) {
                this.classes(sElem, s) //Adds classes
            }
            
            sElem.id = s.id                                         //Set sibling id
            let p    = document.getElementById(this._parents[0].id) //Store parent node
            p.appendChild(sElem)                                    //Append sibling node to parent

            //If sibling has attributes
            if (s.attributes !== undefined && s.attributes.length > 0) {
                this.attributes(sElem, s) //Adds attributes
            }

            //If sibling has further siblings
            if (s.siblings) {

                //Add sibling to parents array
                this._parents.unshift(s)

                //Render siblings
                this.siblings(s.siblings)

                //Remove siblings parent from parents array
                this._parents.shift()

            }

        }

    }

    /**
     * Adds classes to HTML element
     * @param {HTMLElement} elem_ - Reference to HTML element
     * @param {object} n_         - Config object containing classes to add
     */
    classes(elem_, n_) {

        //Loop through classes
        for (let i = 0; i < n_.classList.length; i++) {

            let c = n_.classList[i] //Store class
            elem_.classList.add(c)  //Add class
        }

    }

    /**
     * Adds attributes to HTML element
     * @param {HTMLElement} elem_ - Reference to HTML element
     * @param {object} n_         - Config object containing attributes to add
     */
    attributes(elem_, n_) {

        //Loop through attributes
        for (let i = 0; i < n_.attributes.length; i++) {

            let attributes = n_.attributes[i] //Store attribute

            //Loop through attribute names
            Object.keys(attributes).forEach(a_ => {
                elem_.setAttribute(a_, attributes[a_]) //Set attribute
            })

        }

    }

    /** 
     * Manages styling for button arch section
     * @param {string} v_ - New orientation.
     */
    arch(v_) {

        let o        = ORIENTATIONS.filter(o_ => o_ !== v_) //Retrieve old orientation
        let archNode = document.getElementById(arch)        //Get arch node
        
        archNode.classList.remove(o[0]) //Remove old orientation styling
        archNode.classList.add(v_)      //Add new orientation styling

    }
    
    /** 
     * Manages styling for whether controls are inverted or not
     * @param {boolean}  inverted_ - Whether UI is inverted or not.
     * @param {Button[]} buttons_  - Array of buttons.
     */
    invert(inverted_, buttons_) {

        //Loop through ui sides
        for (let i = 0; i < UI_SIDES.length; i++) {

            //Store side
            let side = UI_SIDES[i]

            //Add/remove inverted styling
            inverted_ ? document.getElementById(side).classList.add('inverted') : document.getElementById(side).classList.remove('inverted')

        }

        //Loop through all buttons
        for (let i = 0; i < buttons_.length; i++) {

            //Store current button
            let button = buttons_[i]

            //Handle inverted UI styling
            inverted_ ? button.elem.classList.add('inverted') : button.elem.classList.remove('inverted')

            //If button has text
            if (button.text) {
                inverted_ ? button.textElem.classList.add('inverted') : button.textElem.classList.remove('inverted') //Apply styling based on inverted controls
            }

            if (button._div) {
                inverted_ ? button._div.classList.add('inverted') : button._div.classList.remove('inverted') //Apply styling based on inverted controls
            }
        }

    }

    /** 
     * Applies styling to hide old UI to not interfere with new UI based on device
     * @param {boolean} mobile_ - Whether device is mobile
     */
    hideOldUI(mobile_) {

        //If mobile
        if (mobile_) {

            let oldConsole = document.getElementById('betConsoleMobile') //Retrieve old mobile console

            //Hide old mobile console
            oldConsole.style.display    = 'none'
            oldConsole.style.visibility = 'hidden'

            let gameHeader = document.getElementsByClassName('game__header')[0] //Retrieve mobile game header

            //Hide mobile game header
            gameHeader.style.display    = 'none'
            gameHeader.style.visibility = 'none'

        }

        //Desktop
        else {

            let oldOverlay = document.getElementsByClassName('overlay__image')[0] //Retrieve desktop overlay
            oldOverlay.style.backgroundImage = 'none';                            //Hide overlay

        }

    }

 }